import React, {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Button, Loader, Modal } from 'ncoded-component-library';
import useCallbackRef from 'hooks/useCallbackRef';
import { getIsArrayEmpty, sortDataRoomNodes } from 'utils';
import FileCheckSelector from '../../../FileCheckSelector';
import {
  ModalProps,
  ModalRef,
} from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import { DataRoomSelectableNode } from '../../../FileCheckSelector/types';
import {
  collectCheckedNodesForDownload,
  getIsShareableNodeDisabled,
} from '../../../FileCheckSelector/utils';
import useNodes from 'hooks/useNodes';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';

import './ShareableFilesSelectModal.styles.scss';

type ShareableFilesSelectModalProps = {
  nodes?: DataRoomSelectableNode[];
  isLoading?: boolean;
  isSubmitting?: boolean;
  initiallyCheckedNodes?: string[];
  onFilesSelect: (nodes: DataRoomSelectableNode[]) => void;
} & ModalProps;

const ShareableFilesSelectModal: React.ForwardRefRenderFunction<
  ModalRef,
  ShareableFilesSelectModalProps
> = (props, ref) => {
  const {
    className,
    nodes,
    isLoading,
    isSubmitting,
    initiallyCheckedNodes = [],
    onFilesSelect,
    ...rest
  } = props;

  const classes = classNames('shareable-files-select-modal', className);

  const [modal, modalRef] = useCallbackRef<ModalRef>(null);

  const [dataRoomNodes, setDataRoomNodes] = useState<DataRoomSelectableNode[]>(
    [],
  );

  const {
    dataRoom: { _id: dataRoomId },
  } = useContext(DataRoomContext);
  const { popError } = useContext(PopNotificationsContext);

  useImperativeHandle(ref, () => modal, [modal]);

  const { t } = useTranslation();

  const { getCopyByNodeType } = useNodes(nodes);

  const title = getCopyByNodeType(
    'selectShareableItems',
    'selectShareableFolders',
    'selectShareableFiles',
  );

  const checkedNodes = useMemo(
    () => collectCheckedNodesForDownload(dataRoomNodes),
    [dataRoomNodes],
  );

  useEffect(() => {
    if (!modal?.openState || isLoading) return;

    if (!nodes?.length) {
      popError(t('noFilesInDataRoom'));
      modal?.close();
      return;
    }

    setDataRoomNodes(sortDataRoomNodes(nodes));

    return () => {
      setDataRoomNodes([]);
    };
  }, [nodes, modal, isLoading, t, popError]);

  return (
    <Modal
      className={classes}
      title={<h2>{title}</h2>}
      ref={modalRef}
      {...rest}
    >
      {(isSubmitting || isLoading) && <Loader />}
      {!isLoading && (
        <FileCheckSelector
          dataRoomNodes={dataRoomNodes}
          buyerLabel={t('onlySellSideFilesWillBeShared')}
          initiallyCheckedNodes={initiallyCheckedNodes}
          setDataRoomNodes={setDataRoomNodes}
          checkIsNodeDisabled={getIsShareableNodeDisabled}
          getChildren={(id: string) =>
            dataRoomApi.nodes.getChildren(dataRoomId, id)
          }
        />
      )}

      <div className="shareable-files-select-modal__actions">
        <Button variant="link" onClick={modal?.close} disabled={isSubmitting}>
          {t('cancel')}
        </Button>
        <Button
          onClick={() => onFilesSelect(dataRoomNodes)}
          disabled={getIsArrayEmpty(checkedNodes) || isLoading || isSubmitting}
        >
          {t('confirm')}
        </Button>
      </div>
    </Modal>
  );
};

export default forwardRef(ShareableFilesSelectModal);
